import styled, { css, keyframes } from 'styled-components';
import { PhoneInput } from '@treedis/tour-loader';

const slideInBottom = keyframes`
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
`;

const slideOutBottom = keyframes`
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(100%);
    }
`;

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.8);
    }
    to {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
    }
`;

const fadeOut = keyframes`
    from {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
    }
    to {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.8);
    }
`;

const fadeInBackground = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const fadeOutBackground = keyframes`
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
    }
`;

export const Background = styled.div<{ open: boolean; isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  backdrop-filter: blur(2px);
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
  animation: ${({ open }) => (open ? fadeInBackground : fadeOutBackground)} 0.3s ease-out forwards;
`;

export const Wrapper = styled.div<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  background: white;
  width: 90vw;
  max-width: 500px;
  height: auto;
  border-radius: 12px;
  padding: 24px;
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
  overflow: hidden;
  ${({ open }) =>
    open
      ? css`
          animation: ${fadeIn} 0.3s ease-out forwards;
        `
      : css`
          animation: ${fadeOut} 0.3s ease-out forwards;
        `}

  @media (max-width: 768px) {
    width: 100vw;
    border-radius: 12px 12px 0 0;
    padding: 14px;
    bottom: 0;
    left: 0;
    top: auto;
    transform: none;

    ${({ open }) =>
      open
        ? css`
            animation: ${slideInBottom} 0.3s ease-out forwards;
          `
        : css`
            animation: ${slideOutBottom} 0.3s ease-out forwards;
          `}
  }
`;

export const Divider = styled.div`
  width: calc(100% + 48px);
  height: 1px;
  background-color: #eaecf0;
  margin: 24px -24px;
  @media (max-width: 768px) {
    width: calc(100% + 28px);
    margin: 14px -14px;
  }
`;

export const IconName = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const IconContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 10px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
`;

export const Icon = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 8px;
`;

export const TextContainer = styled.div<{ rtl?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  direction: ${(props) => (props.rtl ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.rtl ? 'right' : 'left')};
`;

export const PathTypeContainer = styled.div<{ rtl?: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 20px 0px;
  direction: ${(props) => (props.rtl ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.rtl ? 'right' : 'left')};
  @media (max-width: 768px) {
    margin: 14px 0px 10px 0px;
  }
`;

export const PathTypeItemsContainer = styled.div`
  display: flex;
  height: 41px;
  gap: 10px;
`;

export const PathTypeItem = styled.div<{ selected?: boolean; disabled?: boolean; rtl?: boolean }>`
  border-radius: 8px;
  display: flex;
  padding: 10px;
  gap: 6px;
  align-items: center;
  //flex-grow: 1;
  //flex-basis: 0;
  cursor: pointer;
  transition-duration: 0.3s;
  direction: ${(props) => (props.rtl ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.rtl ? 'right' : 'left')};
  background: ${({ selected }) => (selected ? '#2055FF0F' : '')};
  color: ${({ selected }) => (selected ? '#2055FF' : 'black')};
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  svg {
    color: ${(props) => (props.selected ? '#2055ff' : '#667085')};
    transition-duration: 0.5s;
    path {
      //fill: #101828;
      fill: ${(props) => (props.selected ? 'rgba(32, 85, 255, 0.01)' : '#F7F7F7')}; //#667085
      transition-duration: 0.5s;
    }
  }
  &:hover {
    background-color: ${({ selected }) => (selected ? '#2055FF0F' : 'rgba(107, 107, 107, 0.08)')};
    color: #2055ff;
    opacity: 1;
    svg {
      color: #2055ff;
      path {
        fill: rgba(32, 85, 255, 0.01);
        stroke: #2055ff;
      }
    }
  }
  &:first-of-type {
    svg {
      path {
        fill: none;
        //stroke: #101828;
        stroke: ${(props) => (props.selected ? '#2055ff' : '#667085')};
      }
    }
    &:hover {
      svg {
        path {
          fill: none;
          stroke: #2055ff;
        }
      }
    }
  }
`;

PathTypeItem.defaultProps = {
  selected: false,
  disabled: false,
};

export const PathTypeSkeletonItem = styled.div<{ selected?: boolean }>`
  height: 99px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-basis: 0;
  padding: 10px;
  gap: 5px;
  border-radius: 8px;
  background: ${({ selected }) => (selected ? '#2055FF0F' : 'white')};
`;

PathTypeSkeletonItem.defaultProps = {
  selected: false,
};

export const Category = styled.span`
  font-size: 14px;
  color: #667085;
  line-height: 17px;
`;

export const Name = styled.span`
  font-size: 18px;
  font-weight: bold;
  line-height: 24px;
`;

export const CloseButton = styled.button<{ rounded?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: ${(props) => (props.rounded ? '50%' : ' 8px')};
  background: #eaecf0;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-radius: 999rem;
  }
`;

export const Description = styled.p<{ rtl?: boolean }>`
  font-size: 16px;
  color: #667085;
  line-height: 19px;
  direction: ${(props) => (props.rtl ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.rtl ? 'right' : 'left')};
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 17px;
  }
`;

export const Button = styled.button`
  height: 44px;
  width: 100%;
  padding: 10px;
  background-color: #2055ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  cursor: pointer;
`;

export const StyledPhoneInput = styled(PhoneInput)`
  .country-list {
    left: 10px;
    scrollbar-width: thin;
    width: 274px;
    text-align: start;
    position: fixed;
    left: 34px;
    .search {
      padding: 10px 10px 6px 10px;
    }
  }
  .country-list::-webkit-scrollbar {
    width: 8px;
  }
  .country-list::-webkit-scrollbar-thumb {
    background-color: #bdbdbd !important;
  }
`;

export const RatingImage = styled.img`
  cursor: pointer;
  transition: transform 0.3s ease, filter 0.3s ease;
  &:hover {
    transform: translateY(-15px) scale(1.1); /* Jump and enlarge slightly */
  }
`;

const LoadingAnimation = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

export const LoadingButton = styled(Button)<{ loadingProgress: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  gap: 6px;
  overflow: hidden;
  background-color: ${({ loadingProgress }) => (loadingProgress < 100 ? '#C4CCDB66' : '#2055FF')};
  color: ${({ loadingProgress }) => (loadingProgress < 100 ? '#667085' : 'white')};
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ loadingProgress }) => (loadingProgress < 100 ? loadingProgress : 0)}%;
    background-color: rgba(255, 255, 255, 0.3);

    ${({ loadingProgress }) =>
      loadingProgress < 100 &&
      css`
        animation: ${LoadingAnimation} 2s infinite;
      `}
`;

export const generateCollapseStyle = ({ mobilePosition, rtl, filteredItems }) => ({
  maxHeight: { xs: mobilePosition < 2 ? '150px' : `${0.95 * window.innerHeight - 250}px`, sm: '612px' },
  overflowY: filteredItems?.length > 8 ? 'scroll' : 'none',
  transition: 'max-height 0.3s',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#BDBDBD',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#fff',
  },
  '&.animateHeight': { maxHeight: '74px', transition: 'max-height 0.3s' },
  direction: rtl ? 'rtl' : 'ltr',
});

export const choicePopupPointBtn = {
  background: '#ffff',
  color: 'black',
  border: '1px solid #D0D5DD',
  borderRadius: '10px',
  padding: '0px 14px',
  width: '100%',
  height: '50px',
  justifyContent: 'flex-start',
  gap: '10px',
  '&:disabled': {
    background: '#EAECF0',
  },
};
